import React, { useEffect, useState } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

export default function Lightbox({ image, title, description, category, onClose, onPrev, onNext }) {
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // Minimum swipe distance in pixels
  const minSwipeDistance = 50;

  // Prevent background scrolling when lightbox is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && onPrev) onPrev();
      if (e.key === 'ArrowRight' && onNext) onNext();
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = 'unset';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose, onPrev, onNext]);

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && onNext) {
      onNext();
    }
    if (isRightSwipe && onPrev) {
      onPrev();
    }
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex flex-col justify-between bg-stone-950/95 backdrop-blur-md transition-opacity duration-300"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      {/* Top Bar */}
      <div className="flex justify-between items-center px-6 py-4 text-white z-10">
        <div>
          {category && (
            <span className="text-xs uppercase tracking-widest text-emerald-400 font-semibold">
              {category}
            </span>
          )}
          {title && <h3 className="text-lg font-medium text-stone-100">{title}</h3>}
        </div>
        <button
          onClick={onClose}
          className="p-2 rounded-full bg-stone-900/80 hover:bg-stone-800 text-stone-300 hover:text-white transition-colors focus:outline-none cursor-pointer"
          aria-label="Luk visning"
        >
          <X className="w-6 h-6" />
        </button>
      </div>

      {/* Main Image Area */}
      <div className="relative flex-1 flex items-center justify-center p-4">
        {/* Left Arrow */}
        {onPrev && (
          <button
            onClick={onPrev}
            className="absolute left-4 md:left-8 p-3 rounded-full bg-stone-900/60 hover:bg-stone-800 text-stone-300 hover:text-white transition-colors focus:outline-none z-10 cursor-pointer"
            aria-label="Forrige billede"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
        )}

        {/* Media (Image or Video) */}
        {image?.toLowerCase().includes('.mp4') ? (
          <video
            src={image}
            controls
            autoPlay
            className="max-h-[75vh] max-w-full object-contain rounded-lg shadow-2xl animate-fade-in focus:outline-none z-10"
          />
        ) : (
          <img
            src={image}
            alt={title || 'Galleri billede'}
            className="max-h-[75vh] max-w-full object-contain rounded-lg shadow-2xl select-none animate-fade-in"
            draggable={false}
          />
        )}

        {/* Right Arrow */}
        {onNext && (
          <button
            onClick={onNext}
            className="absolute right-4 md:right-8 p-3 rounded-full bg-stone-900/60 hover:bg-stone-800 text-stone-300 hover:text-white transition-colors focus:outline-none z-10 cursor-pointer"
            aria-label="Næste billede"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        )}
      </div>

      {/* Bottom Info Bar */}
      {description && (
        <div className="bg-stone-900/80 border-t border-stone-800/60 px-6 py-6 text-center text-stone-300 max-w-3xl mx-auto w-full rounded-t-xl mb-4">
          <p className="text-sm md:text-base leading-relaxed">{description}</p>
        </div>
      )}
    </div>
  );
}
